import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import React, { KeyboardEvent, MouseEvent, ReactNode } from "react";
import Loader from "src/grading/components/Loader/Loader";

export interface DataTableColumn<T> {
  key: string;
  header: ReactNode;
  align?: "left" | "right" | "center";
  width?: number | string;
  render?: (row: T) => ReactNode;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  getRowKey: (row: T) => string | number;
  "aria-label": string;
  /**
   * Rows in the whole data set, header row included, when `rows` is one page of it. Set with
   * `rowIndexOffset` so each row announces its position in the whole set.
   */
  "aria-rowcount"?: number;
  /** How many data rows precede this page (page × rows per page). */
  rowIndexOffset?: number;
  onRowClick?: (row: T) => void;
  /** Extra `data-` attributes per row, such as a stable `data-testid` for the e2e suite. */
  getRowData?: (row: T) => Record<`data-${string}`, string | undefined>;
  dense?: boolean;
  stickyHeader?: boolean;
  emptyState?: ReactNode;
  loading?: boolean;
  /** Minimum table width in px before the container scrolls horizontally. */
  minWidth?: number;
}

export const DataTable = <T,>({
  columns,
  rows,
  getRowKey,
  onRowClick,
  getRowData,
  dense = false,
  stickyHeader = false,
  emptyState,
  loading = false,
  minWidth = 640,
  "aria-label": ariaLabel,
  "aria-rowcount": ariaRowCount,
  rowIndexOffset = 0,
}: DataTableProps<T>) => {
  const indexed = ariaRowCount !== undefined;
  const handleKeyDown = (event: KeyboardEvent<HTMLElement>, row: T) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onRowClick?.(row);
    }
  };

  const isEmpty = !loading && rows.length === 0;

  return (
    <TableContainer
      data-testid="data-table-container"
      sx={{ overflowX: "auto", maxWidth: "100%" }}
    >
      <Table
        aria-label={ariaLabel}
        aria-rowcount={ariaRowCount}
        size={dense ? "small" : "medium"}
        stickyHeader={stickyHeader}
        sx={{ minWidth }}
      >
        <TableHead>
          <TableRow aria-rowindex={indexed ? 1 : undefined}>
            {columns.map((column) => (
              <TableCell
                key={column.key}
                scope="col"
                align={column.align}
                sx={{ width: column.width }}
              >
                {column.header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        {loading ? (
          <TableBody>
            <TableRow>
              <TableCell colSpan={columns.length}>
                <Loader />
              </TableCell>
            </TableRow>
          </TableBody>
        ) : (
          <TableBody>
            {rows.map((row, index) => (
              <TableRow
                key={getRowKey(row)}
                {...getRowData?.(row)}
                aria-rowindex={indexed ? rowIndexOffset + index + 2 : undefined}
                hover={Boolean(onRowClick)}
                {...(onRowClick
                  ? {
                      role: "button",
                      tabIndex: 0,
                      onClick: (event: MouseEvent<HTMLElement>) => {
                        const interactive = (
                          event.target as HTMLElement
                        ).closest(
                          "a,button,input,select,textarea,[role=button]",
                        );
                        if (
                          interactive &&
                          interactive !== event.currentTarget
                        ) {
                          return;
                        }
                        onRowClick(row);
                      },
                      onKeyDown: (event: KeyboardEvent<HTMLElement>) =>
                        handleKeyDown(event, row),
                    }
                  : {})}
                sx={
                  onRowClick
                    ? {
                        cursor: "pointer",
                        "&:focus-visible": {
                          outline: "2px solid",
                          outlineColor: "primary.main",
                          outlineOffset: "-2px",
                        },
                      }
                    : undefined
                }
              >
                {columns.map((column) => (
                  <TableCell key={column.key} align={column.align}>
                    {column.render
                      ? column.render(row)
                      : // Columns without `render` read a plain field by key; T is unconstrained, so view the row as a record.
                        (row as unknown as Record<string, unknown>)[
                          column.key
                        ]?.toString()}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        )}
      </Table>
      {isEmpty && emptyState ? <Box>{emptyState}</Box> : null}
    </TableContainer>
  );
};

export default DataTable;
